import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import DocStatusTag from '_components/utils/DocStatusTag';
import LayoffModal from '../LayoffModal';
import PdfDocument from '../PdfDocument';
import {
  RadioGroup,
  TextField,
  firstErrors,
  withDefaults,
} from '../FormControls';
import { getFieldErrors, getLayoffStatus, useDownloadPdf } from '../useLayoff';
import {
  useFinalizeLayoff,
  useReview,
  useReviewPreview,
  useSaveReview,
} from './useReview';
import styles from '../layoff.module.scss';

const EMPTY_OFFICE = {
  office_date_received: '',
  office_ldw: '',
  office_accrued_pph: '',
  office_merchandise_expense: '',
  office_boot_stipend: '',
  office_other: '',
  office_other_amount: '',
  office_notified_401k: false,
  office_notified_health: false,
  office_health_cancel_date: '',
  office_notified_garnishment: false,
  office_garnishment: '',
  office_final_check_date: '',
  office_final_check_method: '',
};

// Same limits as the API (ReviewLayoffRequest). Checked here so a value out of range shows a
// message next to its field and never reaches the live preview, which can't render it.
const OFFICE_AMOUNT_LIMITS = {
  office_accrued_pph: { max: 10000, message: 'Enter at most 10,000 hours.' },
  office_merchandise_expense: {
    max: 100000,
    message: 'Enter an amount up to $100,000.',
  },
  office_boot_stipend: {
    max: 100000,
    message: 'Enter an amount up to $100,000.',
  },
  office_other_amount: {
    max: 100000,
    message: 'Enter an amount up to $100,000.',
  },
};

const officeAmountErrors = (office) =>
  Object.fromEntries(
    Object.entries(OFFICE_AMOUNT_LIMITS).flatMap(([name, limit]) => {
      const value = office[name];
      if (value === '' || value === null || value === undefined) return [];
      const number = Number(value);
      if (Number.isNaN(number)) return [[`office.${name}`, 'Enter a number.']];
      if (number < 0) return [[`office.${name}`, 'Enter 0 or more.']];
      if (number > limit.max) return [[`office.${name}`, limit.message]];
      return [];
    }),
  );

const formatDate = (value) =>
  value ? moment(value).format('MMM D, YYYY') : '—';

const ANSWER_LABELS = {
  yes: 'Yes',
  no: 'No',
  unknown: 'Unknown',
  not_enrolled: 'Not enrolled',
  cancel: 'Cancel insurance',
  keep_deduct: 'Keep and deduct 2 months of premiums',
  payment_plan: 'Do not deduct, payment plan',
  pickup: 'Pick up paper check at the Chico office',
  direct_deposit: 'Direct deposit',
  none: 'None to report',
  na: 'N/A',
  hold: 'Held',
  all_turned_in: 'All turned in',
  pending: 'Pending',
  conditional: 'Under conditions',
  all: 'All returned',
  partial: 'Partially returned',
};
const answer = (value) => ANSWER_LABELS[value] ?? value ?? '—';

/**
 * The superintendent's sections 1-5, read-only: they can't be changed at review
 */
const SupervisorAnswers = ({ form }) => {
  const rehire =
    form.rehire_eligible === 'yes'
      ? `Yes, estimated rehire ${formatDate(form.rehire_date)}`
      : form.rehire_eligible === 'conditional'
        ? `Under conditions: ${form.rehire_conditions}`
        : answer(form.rehire_eligible);
  const keyFob = ['yes', 'no'].includes(form.key_fob)
    ? `${answer(form.key_fob)} (fob #${form.key_fob_number || '—'})`
    : answer(form.key_fob);
  const rows = [
    ['Reason for separation', form.reason || '—'],
    ['Timesheets', answer(form.timesheets)],
    ['Eligible for rehire', rehire],
    ['Issued gear', answer(form.gear)],
    [
      'Credit card',
      form.credit_card === 'yes' ? 'Returned' : answer(form.credit_card),
    ],
    ['Driver pin cancelled', answer(form.driver_pin)],
    ['Key fob cancelled or turned in', keyFob],
    ['Alarm & security codes cancelled', answer(form.alarm_codes)],
    ['Entry codes changed', answer(form.entry_codes)],
  ];
  return (
    <dl className={styles.answers}>
      {rows.map(([label, value]) => (
        <React.Fragment key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </React.Fragment>
      ))}
    </dl>
  );
};

/**
 * The employee's own answers ("Confirm with employee"), read-only for the reviewer
 */
const EmployeeAnswers = ({ form }) => {
  const rows = [
    [
      'Mailing address',
      [form.mailing_address_line1, form.mailing_address_line2]
        .filter(Boolean)
        .join(', ') || 'Not changed',
    ],
    ['Phone', form.phone || 'Not changed'],
    ['Returning next year', answer(form.returning_next_year)],
    ['Available off season', answer(form.available_off_season)],
    [
      'Insurance',
      form.insurance === 'cancel'
        ? `Cancel, effective ${formatDate(form.insurance_cancel_date)}`
        : answer(form.insurance),
    ],
    ['Final check', answer(form.final_check)],
    [
      'Workplace injuries',
      form.workplace_injuries === 'yes'
        ? `Yes: ${form.injury_description}`
        : answer(form.workplace_injuries),
    ],
    ['Notes', form.additional_notes || '—'],
  ];
  return (
    <dl className={styles.answers}>
      {rows.map(([label, value]) => (
        <React.Fragment key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </React.Fragment>
      ))}
    </dl>
  );
};

const CheckboxField = ({ id, label, checked, onChange }) => (
  <div className={styles.confirm_row}>
    <input
      id={id}
      type='checkbox'
      checked={!!checked}
      onChange={() => onChange(!checked)}
    />
    <label htmlFor={id}>{label}</label>
  </div>
);

const OfficeFields = ({ office, setOffice, errors }) => {
  const field = (name) => (value) =>
    setOffice((prev) => ({ ...prev, [name]: value }));
  const error = (name) => errors[`office.${name}`];
  return (
    <fieldset className={styles.security_group}>
      <legend className={styles.field_label}>Office Use Only</legend>
      <div className={styles.field_grid}>
        <TextField
          required
          id='office-date-received'
          type='date'
          label="Date rec'd"
          value={office.office_date_received}
          onChange={field('office_date_received')}
          error={error('office_date_received')}
        />
        <TextField
          required
          id='office-ldw'
          type='date'
          label='LDW (last day worked)'
          value={office.office_ldw}
          onChange={field('office_ldw')}
          error={error('office_ldw')}
        />
        <TextField
          required
          id='office-pph'
          type='number'
          min='0'
          max='10000'
          step='0.25'
          label='Accrued PPH on final check (hours)'
          value={office.office_accrued_pph}
          onChange={field('office_accrued_pph')}
          error={error('office_accrued_pph')}
        />
        <TextField
          required
          id='office-merchandise'
          type='number'
          min='0'
          max='100000'
          step='0.01'
          label='Merchandise expense ($)'
          value={office.office_merchandise_expense}
          onChange={field('office_merchandise_expense')}
          error={error('office_merchandise_expense')}
        />
        <TextField
          required
          id='office-boot'
          type='number'
          min='0'
          max='100000'
          step='0.01'
          label='Boot stipend ($)'
          value={office.office_boot_stipend}
          onChange={field('office_boot_stipend')}
          error={error('office_boot_stipend')}
        />
        <TextField
          required
          id='office-other'
          label='Other deduction or addition'
          maxLength={40}
          value={office.office_other}
          onChange={field('office_other')}
          error={error('office_other')}
        />
        <TextField
          required
          id='office-other-amount'
          type='number'
          min='0'
          max='100000'
          step='0.01'
          label='Other amount ($)'
          value={office.office_other_amount}
          onChange={field('office_other_amount')}
          error={error('office_other_amount')}
        />
      </div>

      <span className={styles.field_label}>Notified of termination</span>
      <CheckboxField
        id='office-401k'
        label='401k'
        checked={office.office_notified_401k}
        onChange={field('office_notified_401k')}
      />
      <CheckboxField
        id='office-health'
        label='Health insurance'
        checked={office.office_notified_health}
        onChange={field('office_notified_health')}
      />
      {office.office_notified_health && (
        <div className={styles.sub_field}>
          <TextField
            required
            id='office-health-date'
            type='date'
            label='Cancellation effective'
            value={office.office_health_cancel_date}
            onChange={field('office_health_cancel_date')}
            error={error('office_health_cancel_date')}
          />
        </div>
      )}
      <CheckboxField
        id='office-garnishment'
        label='Garnishment'
        checked={office.office_notified_garnishment}
        onChange={field('office_notified_garnishment')}
      />
      {office.office_notified_garnishment && (
        <div className={styles.sub_field}>
          <TextField
            required
            id='office-garnishment-detail'
            label='Garnishment details'
            maxLength={40}
            value={office.office_garnishment}
            onChange={field('office_garnishment')}
            error={error('office_garnishment')}
          />
        </div>
      )}

      <TextField
        required
        id='office-final-check-date'
        type='date'
        label='Final check date'
        value={office.office_final_check_date}
        onChange={field('office_final_check_date')}
        error={error('office_final_check_date')}
      />
      <RadioGroup
        required
        name='office_final_check_method'
        legend='Final check'
        options={[
          { value: 'direct_deposit', label: 'Direct deposit' },
          { value: 'mailed', label: 'Mailed' },
          { value: 'picked_up', label: 'Picked up' },
        ]}
        value={office.office_final_check_method}
        onChange={field('office_final_check_method')}
        error={error('office_final_check_method')}
      />
    </fieldset>
  );
};

const ReviewLayoff = ({ match }) => {
  const { hash } = match.params;
  const { data: review, isLoading, isError } = useReview(hash);

  const [office, setOffice] = useState(EMPTY_OFFICE);
  const [errors, setErrors] = useState({});
  const [draft, setDraft] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [loadedHash, setLoadedHash] = useState(null);

  // Super Admins and Payroll edit Office Use Only; only Payroll gives the final sign-off
  const canEditOffice = !!review?.can?.edit_office;
  const canFinalize = !!review?.can?.finalize;
  const inReview = canEditOffice || canFinalize;
  const amountErrors = officeAmountErrors(office);
  const amountsValid = Object.keys(amountErrors).length === 0;
  const shownErrors = { ...errors, ...amountErrors };

  // Start from the saved answers once they load
  useEffect(() => {
    if (review && loadedHash !== review.hash) {
      setOffice(withDefaults(EMPTY_OFFICE, review.office));
      setLoadedHash(review.hash);
    }
  }, [review, loadedHash]);

  // The preview follows the form a moment after it changes, once the amounts are in range
  useEffect(() => {
    if (!canEditOffice || !loadedHash || !amountsValid) {
      return undefined;
    }
    const timer = setTimeout(() => setDraft({ office }), 1000);
    return () => clearTimeout(timer);
  }, [canEditOffice, loadedHash, office, amountsValid]);

  const preview = useReviewPreview(
    hash,
    canEditOffice ? draft : null,
    !!review && (!canEditOffice || !!draft),
  );

  const onError = (err) => setErrors(firstErrors(getFieldErrors(err)));
  const save = useSaveReview({ onSuccess: () => setErrors({}), onError });
  const download = useDownloadPdf();
  const finalize = useFinalizeLayoff({
    onSuccess: () => setConfirmOpen(false),
    onError: (err) => {
      setConfirmOpen(false);
      onError(err);
    },
  });

  if (isLoading) {
    return (
      <div className={styles.page_message}>
        <img
          alt='Loading'
          src={require('../../assets/images/loader.gif').default}
        />
      </div>
    );
  }

  if (isError) {
    return (
      <div className={styles.page_message}>
        This lay-off could not be loaded.{' '}
        <Link to='/layoff'>Back to lay-off documents</Link>
      </div>
    );
  }

  const status = getLayoffStatus(review.status);
  // Both lists need an address before the form can be emailed
  const missingLists = [
    review.recipients.payroll.length === 0 && 'Payroll',
    review.recipients.hr.length === 0 && 'HR',
  ].filter(Boolean);
  const listsMissing = missingLists.length > 0;
  const payload = { hash, office };

  return (
    <div className='activity-log'>
      <div className='header'>
        <div className='report_tbl_head sold_rpt_mbl_flx'>
          <h4 className='graph_header'>Review: {review.employee.name}</h4>
          <div className='buttons'>
            <Link to='/layoff' className='button outlined'>
              Back to lay-off documents
            </Link>
          </div>
        </div>
      </div>
      <div className='white'>
        <div className={`inner_container col-lg-12 ${styles.send_layout}`}>
          <section
            aria-label='Document preview'
            className={`${styles.picker} ${styles.document_panel}`}
          >
            <div className={styles.document_head}>
              <span>
                Employee Discharge Form · {review.crew} · sent{' '}
                {formatDate(review.sent_at)} by {review.sent_by}
              </span>
              <DocStatusTag
                status={review.status_label}
                bgColor={status.bg}
                fontColor={status.fg}
                className={styles.status_tag}
              />
            </div>
            {canEditOffice && (
              <p className={styles.hint}>
                Live preview: updates a moment after you change the details.
              </p>
            )}
            {preview.isError && (
              <p>The preview could not be loaded. Please try again.</p>
            )}
            {preview.data ? (
              <PdfDocument file={preview.data} />
            ) : (
              <div className={styles.empty}>Loading preview...</div>
            )}
          </section>

          <aside
            aria-label='Review'
            className={`${styles.summary} ${styles.sign_panel}`}
          >
            <h2 className={styles.summary_title}>Signatures</h2>
            <dl className={styles.signers}>
              <dt>Superintendent</dt>
              <dd>
                {review.signed_by}, {formatDate(review.signed_at)}
              </dd>
              <dt>Employee</dt>
              <dd>
                {review.refusal
                  ? `Refused to sign (recorded by ${review.refusal.by}, ${formatDate(review.refusal.at)})`
                  : `Signed ${formatDate(review.employee_signed_at)}`}
              </dd>
              <dt>Last day worked</dt>
              <dd>{formatDate(review.effective_date)}</dd>
            </dl>
            {review.refusal && (
              <div className={styles.refused_box}>
                Refusal note: {review.refusal.note}
              </div>
            )}

            {!review.refusal && (
              <details className={styles.answers_box}>
                <summary>
                  Employee&apos;s answers (Confirm with employee)
                </summary>
                <EmployeeAnswers form={review.employee_form} />
              </details>
            )}

            {!inReview ? (
              <>
                <p>
                  {review.finalized_at
                    ? `Finalized by ${review.finalized_by} on ${formatDate(review.finalized_at)} and emailed to Payroll and HR.`
                    : review.own_layoff && review.status === 9
                      ? 'This is your own lay-off, so someone else fills in Office Use Only and gives the final sign-off.'
                      : 'This lay-off is not waiting for review.'}
                </p>
                {review.can.download_pdf && (
                  <button
                    type='button'
                    className='button outlined'
                    onClick={() =>
                      download.mutate({
                        hash: review.hash,
                        employeeName: review.employee.name,
                      })
                    }
                    disabled={download.isLoading}
                  >
                    {download.isLoading ? 'Downloading...' : 'Download PDF'}
                  </button>
                )}
              </>
            ) : (
              <>
                <details className={styles.answers_box}>
                  <summary>
                    Superintendent&apos;s answers (sections 1-5)
                  </summary>
                  <SupervisorAnswers form={review.form} />
                </details>

                <h2 className={styles.summary_title}>Office Use Only</h2>
                <p className={styles.hint}>
                  Every field is required before the final sign-off. The
                  superintendent&apos;s and employee&apos;s sections can&apos;t
                  be changed here.
                </p>
                {canEditOffice ? (
                  <OfficeFields
                    office={office}
                    setOffice={setOffice}
                    errors={shownErrors}
                  />
                ) : (
                  <p className={styles.hint}>Payroll fills in this section.</p>
                )}

                <div className={styles.summary_block}>
                  <span className={styles.field_label}>Sent to</span>
                  <span>
                    Payroll:{' '}
                    {review.recipients.payroll.join(', ') || 'no one yet'}
                  </span>
                  <span>
                    HR: {review.recipients.hr.join(', ') || 'no one yet'}
                  </span>
                </div>
                {listsMissing && (
                  <div className={styles.error_banner} role='alert'>
                    Add at least one {missingLists.join(' and ')} email in{' '}
                    <Link to='/settings/system-management'>
                      Settings &gt; System Management &gt; Lay-off Notifications
                    </Link>{' '}
                    before finalizing.
                  </div>
                )}

                {Object.values(shownErrors).some(Boolean) && (
                  <div className={styles.error_text} role='alert'>
                    Some details need attention. See the messages above.
                  </div>
                )}

                {!canFinalize && (
                  <p className={styles.hint}>
                    Final sign-off is given by Payroll
                    {review.finalizers?.length
                      ? ` (${review.finalizers.join(', ')})`
                      : ''}
                    .
                  </p>
                )}
                <div className={styles.summary_actions}>
                  {canEditOffice && (
                    <button
                      type='button'
                      className='button outlined'
                      onClick={() => save.mutate(payload)}
                      disabled={save.isLoading || !amountsValid}
                    >
                      {save.isLoading ? 'Saving...' : 'Save changes'}
                    </button>
                  )}
                  {canFinalize && (
                    <button
                      type='button'
                      className='button'
                      onClick={() => setConfirmOpen(true)}
                      disabled={listsMissing || !amountsValid}
                    >
                      Finalize and send
                    </button>
                  )}
                </div>
              </>
            )}
          </aside>
        </div>
      </div>

      <LayoffModal
        isOpen={confirmOpen}
        title='Give the final sign-off and send?'
        onClose={() => setConfirmOpen(false)}
      >
        <p>
          The form for <strong>{review.employee.name}</strong> is saved as final
          and emailed, with the PDF attached, to {review.sent_by} (who sent it)
          and:
        </p>
        <ul className={styles.confirm_list}>
          {[...review.recipients.payroll, ...review.recipients.hr].map(
            (email) => (
              <li key={email}>{email}</li>
            ),
          )}
        </ul>
        <p className={styles.hint}>
          It can&apos;t be edited after it is finalized.
        </p>
        <div className={styles.modal_actions}>
          <button
            type='button'
            className='button grey'
            onClick={() => setConfirmOpen(false)}
          >
            Cancel
          </button>
          <button
            type='button'
            className='button'
            onClick={() => finalize.mutate(payload)}
            disabled={finalize.isLoading}
          >
            {finalize.isLoading ? 'Sending...' : 'Finalize and send'}
          </button>
        </div>
      </LayoffModal>
    </div>
  );
};

export default ReviewLayoff;
